import "./chartSetup";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import type { ChartOptions } from "chart.js";
import { useMemo } from "react";
import { Bar, Line } from "react-chartjs-2";
import { useFinance } from "../../FinanceContext";
import { metrics, moneyShort, sumTo } from "../../data/model";
import { MONTHS } from "../../data/seedData";

function useGridColors() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return { grid: isDark ? "#2a2f3d" : "#e5e9f2", text: isDark ? "#9aa1b8" : "#69708a" };
}

/** Monthly Budget vs Actual ("burn") bar chart. */
export function BudgetVsActualChart() {
  const { model, scf, C } = useFinance();
  const { grid, text } = useGridColors();
  const { pm, am } = useMemo(() => {
    const pmArr = Array(12).fill(0);
    const amArr = Array(12).fill(0);
    model.items.forEach((it) => {
      it.planned.forEach((v, m) => (pmArr[m] += v * scf));
      it.actual.forEach((v, m) => (amArr[m] += v));
    });
    return { pm: pmArr, am: amArr };
  }, [model, scf]);

  const data = {
    labels: MONTHS,
    datasets: [
      {
        label: "Budget",
        data: pm.map((v) => Math.round(v * C.rate)),
        backgroundColor: "#c7c3ff",
        borderRadius: 5,
      },
      {
        label: "Actual",
        data: am.map((v) => Math.round(v * C.rate)),
        backgroundColor: "#5b53ff",
        borderRadius: 5,
      },
    ],
  };
  const options: ChartOptions<"bar"> = {
    responsive: true,
    maintainAspectRatio: false,
    color: text,
    plugins: {
      legend: { position: "bottom", labels: { color: text } },
      tooltip: {
        callbacks: {
          label: (c) => `${c.dataset.label}: ${moneyShort(Number(c.parsed.y), C.symbol)}`,
        },
      },
    },
    scales: {
      y: {
        ticks: { color: text, callback: (v) => moneyShort(Number(v), C.symbol) },
        grid: { color: grid },
      },
      x: { ticks: { color: text }, grid: { display: false } },
    },
  };
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        Monthly Budget vs Actual (Burn)
      </Typography>
      <Box sx={{ position: "relative", height: 250 }}>
        <Bar data={data} options={options} />
      </Box>
    </Paper>
  );
}

/** Revenue vs Budget line chart. */
export function RevenueVsBudgetChart() {
  const { model, scf, C } = useFinance();
  const { grid, text } = useGridColors();
  const { pm, rvA } = useMemo(() => {
    const pmArr = Array(12).fill(0);
    const rvArr = Array(12).fill(0);
    model.items.forEach((it) => it.planned.forEach((v, m) => (pmArr[m] += v * scf)));
    model.revenue.forEach((r) => r.actual.forEach((v, m) => (rvArr[m] += v)));
    return { pm: pmArr, rvA: rvArr };
  }, [model, scf]);

  const data = {
    labels: MONTHS,
    datasets: [
      {
        label: "Revenue",
        data: rvA.map((v) => Math.round(v * C.rate)),
        borderColor: "#16a34a",
        backgroundColor: "rgba(22,163,74,.12)",
        fill: true,
        tension: 0.35,
      },
      {
        label: "Budget",
        data: pm.map((v) => Math.round(v * C.rate)),
        borderColor: "#5b53ff",
        backgroundColor: "rgba(91,83,255,.10)",
        fill: true,
        tension: 0.35,
      },
    ],
  };
  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { color: text } },
      tooltip: {
        callbacks: {
          label: (c) => `${c.dataset.label}: ${moneyShort(Number(c.parsed.y), C.symbol)}`,
        },
      },
    },
    scales: {
      y: {
        ticks: { color: text, callback: (v) => moneyShort(Number(v), C.symbol) },
        grid: { color: grid },
      },
      x: { ticks: { color: text }, grid: { display: false } },
    },
  };
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        Revenue vs Budget
      </Typography>
      <Box sx={{ position: "relative", height: 250 }}>
        <Line data={data} options={options} />
      </Box>
    </Paper>
  );
}

/** Top 10 budget deviations (horizontal bar). */
export function TopVarianceChart() {
  const { model, selMonth, C } = useFinance();
  const { grid, text } = useGridColors();
  const vs = useMemo(
    () =>
      model.items
        .map((it) => {
          const m = metrics(it, selMonth);
          return { name: it.name, v: m.variance * C.rate };
        })
        .sort((x, y) => Math.abs(y.v) - Math.abs(x.v))
        .slice(0, 10),
    [model, selMonth, C],
  );

  const data = {
    labels: vs.map((x) => (x.name.length > 24 ? x.name.slice(0, 24) + "…" : x.name)),
    datasets: [
      {
        label: "YTD Variance",
        data: vs.map((x) => Math.round(x.v)),
        backgroundColor: vs.map((x) => (x.v >= 0 ? "#16a34a" : "#dc2626")),
        borderRadius: 4,
      },
    ],
  };
  const options: ChartOptions<"bar"> = {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (c) => `${c.dataset.label}: ${moneyShort(Number(c.parsed.x), C.symbol)}`,
        },
      },
    },
    scales: {
      x: {
        ticks: { color: text, callback: (v) => moneyShort(Number(v), C.symbol) },
        grid: { color: grid },
      },
      y: { ticks: { color: text }, grid: { display: false } },
    },
  };
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        Top 10 Budget Deviations
      </Typography>
      <Box sx={{ position: "relative", height: 300 }}>
        <Bar data={data} options={options} />
      </Box>
    </Paper>
  );
}

/** Department performance (budget vs actual), horizontal bar. */
export function DepartmentPerformanceChart() {
  const { model, selMonth, scf, C } = useFinance();
  const { grid, text } = useGridColors();
  const dep = useMemo(() => {
    const d: Record<string, { b: number; a: number }> = {};
    model.items.forEach((it) => {
      d[it.dept] ??= { b: 0, a: 0 };
      const entry = d[it.dept];
      if (!entry) return;
      entry.b += sumTo(it.planned, selMonth) * scf;
      entry.a += sumTo(it.actual, selMonth);
    });
    return d;
  }, [model, selMonth, scf]);
  const depts = Object.keys(dep);

  const data = {
    labels: depts,
    datasets: [
      {
        label: "Budget",
        data: depts.map((d) => Math.round((dep[d]?.b ?? 0) * C.rate)),
        backgroundColor: "#c7c3ff",
        borderRadius: 4,
      },
      {
        label: "Actual",
        data: depts.map((d) => Math.round((dep[d]?.a ?? 0) * C.rate)),
        backgroundColor: "#5b53ff",
        borderRadius: 4,
      },
    ],
  };
  const options: ChartOptions<"bar"> = {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { color: text } },
      tooltip: {
        callbacks: {
          label: (c) => `${c.dataset.label}: ${moneyShort(Number(c.parsed.x), C.symbol)}`,
        },
      },
    },
    scales: {
      x: {
        ticks: { color: text, callback: (v) => moneyShort(Number(v), C.symbol) },
        grid: { color: grid },
      },
      y: { ticks: { color: text }, grid: { display: false } },
    },
  };
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
        Department Performance
      </Typography>
      <Box sx={{ position: "relative", height: 300 }}>
        <Bar data={data} options={options} />
      </Box>
    </Paper>
  );
}
